import { useState, useEffect, useRef } from 'react';
import { useWorkflowConfig, useUpdateWorkflowConfig } from '@/hooks/useAdmin';
import { useToast } from '@/contexts/ToastContext';
import type { ShippingRate } from '@/services/admin';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';

const DEFAULT_RATE: ShippingRate = {
  label: 'Standard Shipping',
  amount: 5.99,
  minDeliveryDays: 5,
  maxDeliveryDays: 8,
};

export const AdminSettingsPage = () => {
  const { data: config, isLoading, isError } = useWorkflowConfig();
  const updateConfig = useUpdateWorkflowConfig();
  const toast = useToast();

  const [autoAdvance, setAutoAdvance] = useState(false);
  const [thresholds, setThresholds] = useState<Record<string, number>>({});
  const [shippingRates, setShippingRates] = useState<ShippingRate[]>([DEFAULT_RATE]);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState<string>('');
  const initializedRef = useRef(false);

  useEffect(() => {
    if (config && !initializedRef.current) {
      initializedRef.current = true;
      setAutoAdvance(config.autoAdvancePaidToQueued);
      setThresholds({ ...config.staleDaysThreshold });
      setShippingRates([...(config.shippingRates.length > 0 ? config.shippingRates : [DEFAULT_RATE])]);
      setFreeShippingThreshold(config.freeShippingThreshold !== null ? String(config.freeShippingThreshold) : '');
    }
  }, [config]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !config) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p style={{ color: 'var(--color-error-500)', fontSize: 'var(--text-sm)' }}>
          Failed to load settings. Please try refreshing the page.
        </p>
      </div>
    );
  }

  const handleSave = () => {
    updateConfig.mutate(
      {
        autoAdvancePaidToQueued: autoAdvance,
        staleDaysThreshold: thresholds,
        shippingRates,
        freeShippingThreshold: freeShippingThreshold.trim() ? parseFloat(freeShippingThreshold) : null,
      },
      {
        onSuccess: () => {
          initializedRef.current = false;
          toast.success('Settings saved');
        },
        onError: () => {
          toast.error('Failed to save settings');
        },
      },
    );
  };

  const updateRate = (index: number, field: keyof ShippingRate, value: string | number | null) => {
    setShippingRates((prev) =>
      prev.map((rate, i) => (i === index ? { ...rate, [field]: value } : rate)),
    );
  };

  const addRate = () => {
    if (shippingRates.length >= 5) return;
    setShippingRates((prev) => [...prev, { label: '', amount: 0, minDeliveryDays: null, maxDeliveryDays: null }]);
  };

  const removeRate = (index: number) => {
    if (shippingRates.length <= 1) return;
    setShippingRates((prev) => prev.filter((_, i) => i !== index));
  };

  const hasChanges =
    config &&
    (autoAdvance !== config.autoAdvancePaidToQueued ||
      JSON.stringify(thresholds) !== JSON.stringify(config.staleDaysThreshold) ||
      JSON.stringify(shippingRates) !== JSON.stringify(config.shippingRates) ||
      (freeShippingThreshold.trim() ? parseFloat(freeShippingThreshold) : null) !== config.freeShippingThreshold);

  return (
    <div>
      <h1
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
          marginBottom: 'var(--space-8)',
        }}
      >
        Settings
      </h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', maxWidth: '600px' }}>
        {/* Shipping Rates */}
        <SettingsCard>
          <h2 style={sectionTitleStyle}>Shipping</h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-4)' }}>
            Shipping options shown to customers at checkout. Stripe handles the charge automatically.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {shippingRates.map((rate, i) => (
              <div
                key={i}
                style={{
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3) var(--space-4)',
                }}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                    Rate {i + 1}
                  </span>
                  {shippingRates.length > 1 && (
                    <span
                      onClick={() => removeRate(i)}
                      style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error-500)', cursor: 'pointer' }}
                    >
                      Remove
                    </span>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 80px', gap: 'var(--space-2)' }}>
                  <div>
                    <label style={labelStyle}>Label</label>
                    <input
                      className="admin-input"
                      value={rate.label}
                      onChange={(e) => updateRate(i, 'label', e.target.value)}
                      placeholder="e.g. Standard Shipping"
                      style={{ fontSize: 'var(--text-sm)' }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Price</label>
                    <div className="flex items-center" style={{ gap: '2px' }}>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>$</span>
                      <input
                        className="admin-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={rate.amount}
                        onChange={(e) => updateRate(i, 'amount', parseFloat(e.target.value) || 0)}
                        style={{ fontSize: 'var(--text-sm)', textAlign: 'right' }}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                  <div>
                    <label style={labelStyle}>Min days</label>
                    <input
                      className="admin-input"
                      type="number"
                      min="1"
                      max="60"
                      value={rate.minDeliveryDays ?? ''}
                      onChange={(e) => updateRate(i, 'minDeliveryDays', e.target.value ? parseInt(e.target.value, 10) : null)}
                      placeholder="--"
                      style={{ fontSize: 'var(--text-sm)' }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Max days</label>
                    <input
                      className="admin-input"
                      type="number"
                      min="1"
                      max="60"
                      value={rate.maxDeliveryDays ?? ''}
                      onChange={(e) => updateRate(i, 'maxDeliveryDays', e.target.value ? parseInt(e.target.value, 10) : null)}
                      placeholder="--"
                      style={{ fontSize: 'var(--text-sm)' }}
                    />
                  </div>
                </div>
              </div>
            ))}

            {shippingRates.length < 5 && (
              <button
                onClick={addRate}
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-accent)',
                  background: 'none',
                  border: '1px dashed var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-2)',
                  cursor: 'pointer',
                }}
              >
                + Add shipping rate
              </button>
            )}
          </div>

          {/* Free shipping threshold */}
          <div style={{ marginTop: 'var(--space-4)' }}>
            <label style={{ ...labelStyle, marginBottom: 'var(--space-1)', display: 'block' }}>
              Free shipping threshold
            </label>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-2)' }}>
              Orders above this amount get free shipping. Leave blank to disable.
            </p>
            <div className="flex items-center" style={{ gap: '4px', maxWidth: '120px' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>$</span>
              <input
                className="admin-input"
                type="number"
                step="1"
                min="0"
                value={freeShippingThreshold}
                onChange={(e) => setFreeShippingThreshold(e.target.value)}
                placeholder="--"
                style={{ fontSize: 'var(--text-sm)' }}
              />
            </div>
          </div>
        </SettingsCard>

        {/* Workflow Automation */}
        <SettingsCard>
          <h2 style={sectionTitleStyle}>Workflow Automation</h2>

          <label
            className="flex items-center"
            style={{ gap: 'var(--space-3)', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={autoAdvance}
              onChange={(e) => setAutoAdvance(e.target.checked)}
              style={{ width: '18px', height: '18px' }}
            />
            <div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                Auto-advance Paid to Queued
              </p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                Automatically move new paid orders to the queue without manual intervention.
              </p>
            </div>
          </label>
        </SettingsCard>

        {/* Stale Order Thresholds */}
        <SettingsCard>
          <h2 style={sectionTitleStyle}>Stale Order Thresholds</h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-4)' }}>
            Number of days before an order in a given status is flagged as stale on the dashboard.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {['paid', 'queued', 'printing', 'post_processing', 'packed'].map((status) => (
              <div key={status} className="flex items-center justify-between">
                <label
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-secondary)',
                    textTransform: 'capitalize',
                  }}
                >
                  {status.replace(/_/g, ' ')}
                </label>
                <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={thresholds[status] ?? 7}
                    onChange={(e) =>
                      setThresholds({ ...thresholds, [status]: parseInt(e.target.value, 10) || 1 })
                    }
                    className="admin-input"
                    style={{
                      width: '60px',
                      textAlign: 'center',
                    }}
                  />
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>days</span>
                </div>
              </div>
            ))}
          </div>
        </SettingsCard>

        {/* Save */}
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateConfig.isPending}
          style={{ width: '100%' }}
        >
          {updateConfig.isPending ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

const SettingsCard = ({ children }: { readonly children: React.ReactNode }) => (
  <div
    style={{
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--bg-surface)',
      padding: 'var(--space-6)',
    }}
  >
    {children}
  </div>
);

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--text-lg)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--text-primary)',
  marginBottom: 'var(--space-4)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-xs)',
  color: 'var(--text-tertiary)',
  display: 'block',
  marginBottom: '2px',
};
